import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Clock3, PackageCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useSocket } from '../contexts/SocketContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  new: 'Placed',
  cooking: 'Preparing',
  ready: 'Ready',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function OrderHistory() {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/my`);
      setOrders(response.data || []);
      sessionStorage.setItem('prefetched_orders', JSON.stringify(response.data || []));
    } catch (error) {
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const prefetched = sessionStorage.getItem('prefetched_orders');
    if (prefetched) {
      try {
        setOrders(JSON.parse(prefetched));
        setIsLoading(false);
      } catch (error) {
        sessionStorage.removeItem('prefetched_orders');
      }
    }
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const pollId = setInterval(fetchOrders, 8000);
    return () => clearInterval(pollId);
  }, [fetchOrders]);

  useEffect(() => {
    if (!socket) return;
    socket.on('order_updated', fetchOrders);
    return () => socket.off('order_updated', fetchOrders);
  }, [socket, fetchOrders]);

  const statusBadgeClass = useMemo(() => ({
    cooking: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    ready: 'bg-green-100 text-green-800 border-green-300',
    completed: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
    new: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  }), []);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="rounded-3xl border border-slate-200/70 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold md:text-3xl">My Orders</h1>
              <p className="mt-1 text-sm text-slate-500">Track status and review everything you've ordered.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate('/')} className="rounded-xl">
                <ArrowLeft className="mr-1 h-4 w-4" /> Home
              </Button>
              <Button variant="outline" onClick={() => navigate('/cart')} className="rounded-xl">
                <ShoppingCart className="mr-1 h-4 w-4" /> Cart
              </Button>
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((key) => (
              <Card key={key} className="rounded-2xl shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {!isLoading && orders.map((order) => {
          const status = STATUS_LABELS[order.status] || order.status;
          const showTrack = order.status !== 'completed' && order.order_number;
          const orderedAt = order.created_at || order.updated_at;
          const progressSteps = ['Placed', 'Preparing', 'Ready', 'Completed'];
          const currentProgress = Math.max(
            progressSteps.indexOf(status),
            status === 'Cancelled' ? 0 : 0,
          );
          return (
            <Card key={order.id} className="rounded-2xl shadow-sm border-0 bg-white/90 dark:bg-slate-900/80">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span className="text-base md:text-lg">Order ID #{order.order_number || order.id?.slice(0, 8)}</span>
                  <Badge className={`border ${statusBadgeClass[order.status] || 'bg-slate-100 text-slate-700'}`}>{status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <p><strong>Total:</strong> ₹{Number(order.total || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" />{orderedAt ? new Date(orderedAt).toLocaleString() : '-'}</p>
                </div>

                <div className="space-y-2">
                  {(order.items || []).map((item) => (
                    <div key={`${order.id}-${item.item_id}`} className="flex items-center justify-between rounded-xl border px-3 py-2 bg-slate-50 dark:bg-slate-900">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.image_url || '/logo.png'}
                          alt={item.title || item.name}
                          className="h-12 w-12 rounded-lg object-cover border"
                        />
                        <p className="font-medium">{item.title || item.name}</p>
                      </div>
                      <p className="text-slate-500">x{item.quantity}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-4 gap-2 text-xs">
                  {progressSteps.map((step, index) => (
                    <div key={step} className="flex flex-col items-center gap-1">
                      <div className={`h-2 w-full rounded-full ${index <= currentProgress ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                      <span className={index <= currentProgress ? 'text-green-600 font-medium' : 'text-slate-500'}>{step}</span>
                    </div>
                  ))}
                </div>

                {showTrack && (
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate(`/track?order=${order.order_number}`)}>
                    <PackageCheck className="mr-1 h-4 w-4" />
                    Track Order
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
        {!isLoading && orders.length === 0 && <p className="text-slate-500">No orders found.</p>}
      </div>
    </div>
  );
}
